/**
 * @fileoverview The tray's one understanding effect (B88 Part A; before that, audit batch 18, A103/A104).
 *
 * Asks the server about every photo not yet understood (POST /tray/guess, newest groups first,
 * limits.TRAY_GUESS_BATCH to a request). The server reads seed packets in the same request, so the answer carries the
 * guess and, for a packets photo, its reading: the app asks for no second reading. One case is asked again: a packets
 * photo the server deferred for the photo help's allowance (the guess saw packets, no reading on the row) goes in the
 * same effect, once a visit, so its card leaves "reading". The answers go into the tray and the tray is read again.
 * Nothing is asked of a gardener without the photo AI. Failures on our side go to Sentry (reportServerFailure) and the
 * cards fall back to asking plainly; a refusal for the photo help's allowance (D23) is kept for AiLimitNotice.
 * A photo the server was sure of and filed onto its plant by itself (B160, `filed` in the answer) is said in a toast
 * each (sayFiled) before the tray is read again; it is no longer on the tray, so nothing is asked about it again.
 *
 * Where: PhotoTray (the route /plants/photos) and the add pop-ups' Check and save, through TrayPiles.
 * API: POST /tray/guess (understandTray: the photos and, from a server that files by itself, `filed`).
 * Settings: limits.TRAY_GUESS_BATCH (lib/growSettings); TRAY_MOVE_UNDO_TOAST_MS (config/tunables/garden), the length of a filed photo's toast.
 *
 * @module useTrayGuesses
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { aiLimitOf, type AiLimitRefusal } from '@/features/billing/lib/aiLimit';
import { refreshAiAllowance } from '@/features/billing/lib/useAiAllowance';
import { understandTray, type TrayPhoto } from '@/shared/api/tray';
import { sayFiled } from './sayFiled';
import { TRAY_MOVE_UNDO_TOAST_MS } from '@/config/tunables/garden';
import { debugLog } from '@/config/config';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { TRAY_QUERY_KEY, putTrayPhotos, type DrawnPile } from './useTray';
import { useGrowLimits } from '@/shared/lib/growSettings';

/** What useTrayGuesses is given. */
interface UseTrayGuessesArgs {
  /** The piles with photos still to sort. */
  piles: DrawnPile[];
  /** True while a batch is being uploaded: piles are still forming, so nothing is asked. */
  uploading: boolean;
  /** Whether the gardener has the photo AI (Free, a paused plan or AI switched off: no). */
  aiAllowed: boolean;
}

/** What useTrayGuesses gives back. */
interface UseTrayGuessesResult {
  /** Puts the model's answers into the tray and reads the tray again. */
  putPhotos: (updated: TrayPhoto[]) => void;
  /** Photos whose guess was given up on: their cards ask plainly. */
  guessFailed: Set<number>;
  /** The photo help's refusal a request last met (D23), said above the piles. */
  aiLimit: AiLimitRefusal | null;
  /** Photos whose request is out right now: a packets photo among them is "reading" on its card. */
  understanding: Set<number>;
  /** Photos of seed packets whose reading could not be had: asked about as plants instead. */
  packetsFailed: Set<number>;
  /** Adds a photo to those the packet reader could not be asked about. */
  setPacketsFailed: React.Dispatch<React.SetStateAction<Set<number>>>;
}

/**
 * A set without these ids.
 *
 * @param set - The set.
 * @param ids - The ids to leave out.
 * @returns A new set.
 */
function withoutIds(set: Set<number>, ids: number[]): Set<number> {
  const next = new Set(set);
  ids.forEach((id) => next.delete(id));
  return next;
}

/**
 * The one understanding effect for the tray's piles.
 *
 * @param args - UseTrayGuessesArgs.
 * @returns UseTrayGuessesResult.
 */
export function useTrayGuesses({ piles, uploading, aiAllowed }: UseTrayGuessesArgs): UseTrayGuessesResult {
  const queryClient = useQueryClient();
  const limits = useGrowLimits();
  /*
   * Photos of seed packets the reader could not be asked about (refused, or
   * the request failed): asked about as plants instead, as if it had found none.
   */
  const [packetsFailed, setPacketsFailed] = React.useState<Set<number>>(() => new Set());
  const [guessFailed, setGuessFailed] = React.useState<Set<number>>(() => new Set());
  /** The photo help's refusal a request last met (D23), said above the piles. */
  const [aiLimit, setAiLimit] = React.useState<AiLimitRefusal | null>(null);
  /** The photos whose request is out right now. */
  const [understanding, setUnderstanding] = React.useState<Set<number>>(() => new Set());

  /**
   * Puts the model's answers into the tray as it stands, then reads the tray
   * again: what Grow saw can move a photo to a pile of its own, and names the pile.
   *
   * @param updated - The photos as the server now has them.
   */
  const putPhotos = (updated: TrayPhoto[]) => {
    putTrayPhotos(queryClient, updated);
    void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
  };

  /*
   * Ask about every photo not yet understood, the newest groups first, and about a packets photo the server left
   * unread (deferred for the photo help's allowance). Once each in a visit: the server marks a photo asked whatever
   * the answer, so a photo the model could not name is asked plain rather than asked again; the next visit asks a
   * deferred reading once more, which lets it catch up when the allowance returns. limits.TRAY_GUESS_BATCH photos to a
   * request (the server's cap), one request at a time; when one fails, the rest are not sent.
   */
  /** The photos already sent in this visit, so none is sent twice. */
  const askedRef = React.useRef<Set<number>>(new Set());
  React.useEffect(() => {
    // Piles are still forming while a batch goes in; asking now would ask
    // about a pile whose lead is about to change, and then ask again.
    if (uploading) return;
    /*
     * No point asking a question we will be refused: without this every pile fired a request that came back 402,
     * which put the whole tray behind the subscription wall the moment it loaded.
     */
    if (!aiAllowed) return;
    // The most photos one request may ask about is the server's (limits.TRAY_GUESS_BATCH); until the app has heard it, none is sent.
    const batchSize = limits.TRAY_GUESS_BATCH;
    if (!batchSize) return;
    // Packets photos the server saw but has not read (the lead's card is the one that shows the reading).
    const unread = new Set(
      piles.map((pile) => pile.lead).filter((lead) => lead.guess.packets === true && !lead.packets).map((lead) => lead.tray_id),
    );
    const pending = piles
      .flatMap((pile) => pile.photos)
      .filter((photo) => (!photo.guess.done || unread.has(photo.tray_id)) && !askedRef.current.has(photo.tray_id))
      .map((photo) => photo.tray_id);
    if (pending.length === 0) return;
    pending.forEach((id) => askedRef.current.add(id));
    setUnderstanding((current) => new Set([...current, ...pending]));
    void (async () => {
      for (let start = 0; start < pending.length; start += batchSize) {
        const batch = pending.slice(start, start + batchSize);
        try {
          const { photos: understood, filed } = await understandTray(batch);
          // Photos Grow was sure of and filed by itself (B160, the server's `filed`): said in a toast each, before the tray is read again.
          if (filed && filed.length > 0) sayFiled(queryClient, filed, TRAY_MOVE_UNDO_TOAST_MS);
          putPhotos(understood);
          refreshAiAllowance(queryClient);
          setUnderstanding((current) => withoutIds(current, batch));
        } catch (error) {
          debugLog('PhotoTray', 'Understanding failed', error);
          // Over the photo help's allowance: said above the piles, with when some comes back.
          const refused = aiLimitOf(error);
          if (refused) setAiLimit(refused);
          // On our side: told to Sentry; the cards still ask plainly.
          if (isServerFailure(error)) reportServerFailure(error, 'Tray guess');
          // Stop the cards waiting on answers that are not coming: they ask plainly, as a plant.
          const rest = pending.slice(start);
          setGuessFailed((current) => new Set([...current, ...rest]));
          setPacketsFailed((current) => new Set([...current, ...rest.filter((id) => unread.has(id))]));
          setUnderstanding((current) => withoutIds(current, rest));
          return;
        }
      }
    })();
    // putPhotos is stable enough for this: it closes over queryClient only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piles, uploading, aiAllowed, limits.TRAY_GUESS_BATCH]);

  return { putPhotos, guessFailed, aiLimit, understanding, packetsFailed, setPacketsFailed };
}
